import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  Group,
  Paper,
  Stack,
  ThemeIcon,
  Title,
  Badge,
  ScrollArea,
  Modal,
  Button,
  Box,
  Text,
} from "@mantine/core"
import {
  IconArrowsMaximize,
  IconChevronDown,
  IconChevronUp,
} from "@tabler/icons-react"
import { useState } from "react"

import { formatNumber } from "@costory/shared/utils/format"

import { CustomMarkdown } from "@costory/front/components/MarkdownEditor"
import DynamicIcon from "@costory/front/components/icons/DynamicIcon"

export type FactorProps = {
  key: number
  title: string
  cost: number
  percentage: number
  status: string
  date: string
  icon?: string
  summary: string
  plainSummary?: string
  badges: {
    key: string
    value: string
    color: string
  }[]
  details: string
}

export const AccordionTile = ({
  factor,
  isExpanded = false,
}: {
  factor: FactorProps
  isExpanded?: boolean
}) => {
  const [showFullAnalysis, setShowFullAnalysis] = useState(false)
  const imageMarkdown = factor.details.match(/\[!\[img\][^\n]*/)?.[0] || ""

  // Create a clean copy of badges to prevent mutation issues
  const originalBadges = [...factor.badges]

  // Limit badges to first 7 when collapsed, show all when expanded
  const visibleBadges = isExpanded ? originalBadges : originalBadges.slice(0, 7)
  const hasMoreBadges = !isExpanded && originalBadges.length > 7
  return (
    <AccordionItem
      key={factor.key}
      value={factor.key.toString()}
      style={{
        borderRadius: 10,
        border: "2px solid var(--mantine-color-border-0)",
        borderLeft: "4px solid var(--mantine-color-primaryHighlight-0)",
      }}
    >
      <Paper
        mt={2}
        p={2}
        style={{ overflow: "hidden", position: "relative", minHeight: "80px" }}
      >
        <Box pos="relative">
          {imageMarkdown && (
            <Group
              pos="absolute"
              right={10}
              bottom={10}
              gap={8}
              align="center"
              style={{ zIndex: 10, pointerEvents: "none" }}
            >
              <Text size="xs" c="dimmed" fw={500}>
                {isExpanded ? "Collapse" : "Expand"}
              </Text>
              {isExpanded ? (
                <IconChevronUp size={16} />
              ) : (
                <IconChevronDown size={16} />
              )}
            </Group>
          )}
          <Accordion.Control
            chevron={null}
            styles={{
              chevron: { display: "none" },
            }}
          >
            <Group justify="space-between">
              <Stack>
                <Group wrap="nowrap">
                  <ThemeIcon
                    size={24}
                    color={factor.status === "closed" ? "green" : "orange"}
                  >
                    <DynamicIcon iconName={factor.icon} size={24} />
                  </ThemeIcon>
                  <Stack gap="xs" w="100%" style={{ minHeight: "60px" }}>
                    <Title order={3} style={{ overflow: "hidden" }}>
                      {factor.title}
                    </Title>
                    <Group
                      pos="absolute"
                      right={20}
                      top={10}
                      gap="xs"
                      align="center"
                    >
                      <Title
                        order={6}
                        c={factor.cost > 0 ? "red.6" : "green.6"}
                      >
                        {formatNumber({ value: factor.cost })}
                      </Title>
                      <Title
                        order={6}
                        c={factor.percentage > 0 ? "red.6" : "green.6"}
                      >
                        {formatNumber({
                          value: factor.percentage,
                          numberType: "percent",
                        })}
                      </Title>
                    </Group>
                    <ScrollArea
                      styles={{
                        root: { maxWidth: "100%", overflowX: "hidden" },
                        scrollbar: {
                          display: "none",
                        },
                      }}
                    >
                      <Group gap={8} wrap="wrap">
                        {visibleBadges.map((badge, index) => (
                          <Badge
                            key={`${factor.key}-${badge.key}-${index}`}
                            color={badge.color}
                            style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                          >
                            {badge.value}
                          </Badge>
                        ))}
                        {hasMoreBadges && (
                          <Badge
                            color="dimmed"
                            style={{ whiteSpace: "nowrap", flexShrink: 0 }}
                          >
                            +{originalBadges.length - 7} more
                          </Badge>
                        )}
                      </Group>
                    </ScrollArea>

                    {factor.plainSummary && (
                      <Title c="textMuted" order={6}>
                        {factor.plainSummary}
                      </Title>
                    )}
                  </Stack>
                </Group>
              </Stack>
            </Group>
          </Accordion.Control>
        </Box>
        {imageMarkdown && (
          <AccordionPanel>
            <Stack>
              <CustomMarkdown
                content={imageMarkdown}
                imgWidth="100%"
                maxWidth="600px"
              />

              <Group justify="flex-end">
                <Button
                  variant="filled"
                  size="xs"
                  onClick={() => setShowFullAnalysis(true)}
                  rightSection={<IconArrowsMaximize size={16} />}
                >
                  Full Analysis
                </Button>
              </Group>
            </Stack>
            <Modal
              opened={showFullAnalysis}
              onClose={() => setShowFullAnalysis(false)}
              title={<strong>Analysis Preview</strong>}
              size="90%"
            >
              <ScrollArea h="60vh">
                <CustomMarkdown
                  content={factor.details}
                  imgWidth="100%"
                  maxWidth="800px"
                />
              </ScrollArea>
            </Modal>
            {/* <List m={2} withPadding>
            {factor.details.map((detail) => (
              <ListItem key={detail.text}>
                <Stack>
                  <Text c="textPrimary">{detail.text}</Text>
                  {detail.img && (
                    <Image mb={10} src={detail.img} alt={detail.text} w={800} />
                  )}
                </Stack>
              </ListItem>
            ))}
          </List> */}
          </AccordionPanel>
        )}
      </Paper>
    </AccordionItem>
  )
}
